import { useState } from "react";
import { toast } from "react-toastify";
import type { Deployment, DeployServiceName, DiffPull } from "@rebuild/shared";
import { SERVICE_LABELS } from "../types";
import { promoteService } from "../api";

const MIN_SOAK_MINUTES = 30;

export function PromoteDialog({
  service,
  deployment,
  selectedPull,
  includedPulls,
  onClose,
  onPromoted,
}: {
  service: DeployServiceName;
  deployment: Deployment;
  selectedPull: DiffPull | null;
  includedPulls: DiffPull[];
  onClose: () => void;
  onPromoted: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const [skipSoak, setSkipSoak] = useState(false);

  const deploymentCreatedAt = new Date(deployment.createdAt).getTime();
  const soakMinutes = Number.isFinite(deploymentCreatedAt)
    ? Math.floor((Date.now() - deploymentCreatedAt) / 60_000)
    : null;
  const isSoakShort = soakMinutes !== null && soakMinutes < MIN_SOAK_MINUTES;
  const sha = deployment.commitSha?.slice(0, 7) ?? "unknown";

  async function handleConfirm() {
    setLoading(true);
    try {
      await promoteService(
        service === "api"
          ? { service, imageRef: deployment.artifactRef ?? undefined }
          : { service, deploymentId: deployment.id },
      );
      toast.success(`${SERVICE_LABELS[service]} promoted to production`);
      onPromoted();
    } catch (e) {
      toast.error(`Promotion failed: ${e instanceof Error ? e.message : "Unknown error"}`);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
      <div className="bg-white border border-gray-200 rounded-lg max-w-md w-full shadow-lg">
        <div className="px-5 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Promote {SERVICE_LABELS[service]} to Production
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {selectedPull
              ? "This will deploy through the selected row: the selected change and every older row below it."
              : "This will deploy the current staging build to production."}
          </p>
        </div>

        <div className="px-5 py-4 space-y-4">
          {selectedPull && (
            <div>
              <p className="text-sm text-gray-500 mb-1">Selected change</p>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-indigo-600 shrink-0">#{selectedPull.number}</span>
                <span className="text-sm text-gray-900 truncate">{selectedPull.title}</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Includes this row and all older rows below it in the changes list.
              </p>
            </div>
          )}

          {includedPulls.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-gray-900">Included in this release</p>
                <span className="text-xs text-gray-400">
                  {includedPulls.length} PR{includedPulls.length !== 1 ? "s" : ""}
                </span>
              </div>
              <div className="rounded-md border border-gray-200 bg-gray-50 max-h-56 overflow-y-auto">
                {includedPulls.map((pull, index) => (
                  <div
                    key={pull.number}
                    className={`px-3 py-2 ${index > 0 ? "border-t border-gray-200" : ""}`}
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-xs font-medium text-indigo-600 shrink-0">#{pull.number}</span>
                      <span className="text-xs text-gray-900 leading-5">{pull.title}</span>
                    </div>
                    <div className="text-[11px] text-gray-400 mt-0.5">
                      {pull.user} · {new Date(pull.mergedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <p className="text-sm text-gray-500 mb-1">Deploying</p>
            <div className="flex items-center gap-2">
              <code className="text-sm font-mono text-gray-900 bg-gray-100 px-1.5 py-0.5 rounded">{sha}</code>
              {deployment.commitMessage && (
                <span className="text-sm text-gray-500 truncate">{deployment.commitMessage}</span>
              )}
            </div>
          </div>

          {deployment.artifactRef && (
            <div>
              <p className="text-sm text-gray-500 mb-1">Artifact</p>
              <code className="text-xs font-mono text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded break-all">
                {deployment.artifactRef}
              </code>
            </div>
          )}

          {isSoakShort && (
            <div className="rounded-lg border border-yellow-300 bg-yellow-50 p-3">
              <p className="text-sm font-medium text-yellow-800">Short soak time ({soakMinutes}m)</p>
              <p className="text-xs text-yellow-700 mt-1">
                Minimum recommended: {MIN_SOAK_MINUTES} minutes.
              </p>
              <label className="flex items-center gap-2 mt-2 text-xs text-yellow-700 cursor-pointer">
                <input type="checkbox" checked={skipSoak} onChange={(e) => setSkipSoak(e.target.checked)} />
                Skip soak check (hotfix)
              </label>
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onClose} disabled={loading} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors">
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading || (isSoakShort && !skipSoak)}
            className="px-4 py-2 text-sm font-medium bg-indigo-600 hover:bg-indigo-500 disabled:bg-gray-100 disabled:text-gray-400 text-white rounded-md transition-colors"
          >
            {loading ? "Promoting..." : "Promote to Production"}
          </button>
        </div>
      </div>
    </div>
  );
}
